import { type MouseEvent, memo, type ReactNode } from 'react';

import { Badge } from '@air/primitive-badge';

import { WorkspaceAgentGuidedVariantSizeRowButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeRowButton';
import { WorkspaceAgentGuidedVariantSizeSelectionMark } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeSelectionMark';
import { WorkspaceAgentGuidedVariantSizeShape } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeShape';
import { type WorkspaceAgentSmartResizeChannelSize } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

export type WorkspaceAgentGuidedVariantSizeOptionRowProps = {
  channel: WorkspaceAgentSmartResizeChannelSize;
  isSelected: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  'data-testid'?: string;
  label?: ReactNode;
  leading?: ReactNode;
  /** `null` hides meta; omit to use channel pixel dimensions when width and height are non-zero. */
  meta?: string | null;
  setId?: string;
  showSelectionMark?: boolean;
};

export const WorkspaceAgentGuidedVariantSizeOptionRow = memo(
  ({
    channel,
    isSelected,
    onClick,
    'data-testid': testId = 'WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_OPTION',
    label,
    leading,
    meta,
    setId,
    showSelectionMark,
  }: WorkspaceAgentGuidedVariantSizeOptionRowProps) => {
    const pixels = `${channel.width} × ${channel.height}`;
    const resolvedLabel = label ?? channel.shortLabel;
    const resolvedBadge = channel.ratio !== pixels ? channel.ratio : undefined;
    let resolvedMeta: string | undefined;

    if (meta === null) {
      resolvedMeta = undefined;
    } else if (meta !== undefined) {
      resolvedMeta = meta;
    } else if (channel.width > 0 && channel.height > 0) {
      resolvedMeta = pixels;
    }

    const resolvedLeading =
      leading ??
      (channel.width > 0 && channel.height > 0 ? (
        <WorkspaceAgentGuidedVariantSizeShape height={channel.height} width={channel.width} />
      ) : null);

    const resolvedShowSelectionMark = showSelectionMark ?? isSelected;

    return (
      <WorkspaceAgentGuidedVariantSizeRowButton
        aria-pressed={isSelected}
        data-set-id={setId}
        data-size-id={setId ? undefined : channel.id}
        data-testid={testId}
        isSelected={isSelected}
        onClick={onClick}
      >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          {resolvedLeading}
          <span className="truncate">{resolvedLabel}</span>
          {resolvedBadge ? (
            <Badge color="gray" size="small">
              {resolvedBadge}
            </Badge>
          ) : null}
          {resolvedMeta ? (
            <span className="shrink-0 ads:text-metadata text-12 text-gray-9 tabular-nums">{resolvedMeta}</span>
          ) : null}
        </span>
        {resolvedShowSelectionMark ? <WorkspaceAgentGuidedVariantSizeSelectionMark /> : null}
      </WorkspaceAgentGuidedVariantSizeRowButton>
    );
  },
);

WorkspaceAgentGuidedVariantSizeOptionRow.displayName = 'WorkspaceAgentGuidedVariantSizeOptionRow';
